import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  loadQueue,
  selectQueue,
  selectQueueTotal,
  selectQueueView,
  selectLoadingQueue,
  selectUwError,
  selectQueuePageSize,
  selectQueuePage
} from '../../../redux/slices/underwriterSlice';
import { selectUser } from '../../../redux/slices/authSlice';
import StatusPill from '../../../components/statusPill';
import { formatRupees } from '../../../utils/loanMath';
import './index.scss';

const VIEWS = [
  { key: 'unclaimed', label: 'Unclaimed' },
  { key: 'mine', label: 'Assigned to me' },
  { key: 'all', label: 'All open' },
  { key: 'decided', label: 'Decided' }
];

const PURPOSE_LABEL = {
  HOME_RENOVATION: 'Home renovation',
  MEDICAL_EMERGENCY: 'Medical emergency',
  EDUCATION: 'Education',
  VEHICLE_PURCHASE: 'Vehicle purchase',
  BUSINESS_EXPANSION: 'Business expansion',
  DEBT_CONSOLIDATION: 'Debt consolidation'
};

// How long an application has been waiting. Ageing is the thing an
// underwriter should notice first — a file sitting for days is a problem.
const waitingSince = (iso) => {
  if (!iso) return '—';
  const hours = Math.floor((Date.now() - new Date(iso).getTime()) / 36e5);

  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return days === 1 ? 'Yesterday' : `${days} days ago`;
};

const isStale = (iso) => {
  if (!iso) return false;
  return (Date.now() - new Date(iso).getTime()) / 36e5 > 48;
};

// FOIR bands. Above 50% is where policy starts pushing back.
const foirBand = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 'none';
  if (n > 50) return 'high';
  if (n > 40) return 'watch';
  return 'ok';
};

const QueueRow = ({ item, currentUserId, onOpen }) => {
  const mine = item.assignedTo?.id === currentUserId;
  const claimed = Boolean(item.assignedTo);
  const band = foirBand(item.foirPct);

  return (
    <button type="button" className="q-row" onClick={() => onOpen(item.id)}>
      <span className="q-row__ref">
        <span className="q-row__ref-no numeric">{item.referenceNo}</span>
        <span className={`q-row__age ${isStale(item.submittedAt) ? 'q-row__age--stale' : ''}`}>
          {waitingSince(item.submittedAt)}
        </span>
      </span>

      <span className="q-row__applicant">
        <span className="q-row__name">{item.applicantName || 'Unnamed applicant'}</span>
        <span className="q-row__purpose">{PURPOSE_LABEL[item.loanPurpose] ?? '—'}</span>
      </span>

      <span className="q-row__amount">
        <span className="q-row__amount-value numeric">{formatRupees(item.loanAmount)}</span>
        <span className="q-row__amount-sub numeric">{item.tenureMonths} months</span>
      </span>

      <span className="q-row__emi numeric">{formatRupees(item.emi)}</span>

      <span className={`q-row__foir q-row__foir--${band}`}>
        {item.foirPct != null ? `${Number(item.foirPct).toFixed(1)}%` : '—'}
      </span>

      <span className="q-row__status">
        <StatusPill status={item.status} size="sm" />
      </span>

      <span className="q-row__owner">
        {claimed ? (
          <span className={`q-row__owner-tag ${mine ? 'q-row__owner-tag--mine' : ''}`}>
            {mine ? 'You' : item.assignedTo.username}
          </span>
        ) : (
          <span className="q-row__owner-open">Open</span>
        )}
      </span>
    </button>
  );
};

QueueRow.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    referenceNo: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    applicantName: PropTypes.string,
    loanAmount: PropTypes.string,
    tenureMonths: PropTypes.number,
    loanPurpose: PropTypes.string,
    emi: PropTypes.string,
    foirPct: PropTypes.string,
    submittedAt: PropTypes.string,
    assignedTo: PropTypes.shape({
      id: PropTypes.string,
      username: PropTypes.string
    })
  }).isRequired,
  currentUserId: PropTypes.string,
  onOpen: PropTypes.func.isRequired
};

const QueuePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const queue = useSelector(selectQueue);
  const total = useSelector(selectQueueTotal);
  const view = useSelector(selectQueueView);
  const loading = useSelector(selectLoadingQueue);
  const error = useSelector(selectUwError);
  const user = useSelector(selectUser);

  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(loadQueue({ view: 'unclaimed' }));
  }, [dispatch]);

  // Debounced search — one request when typing stops, not one per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(loadQueue({ view, search }));
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const changeView = (nextView) => {
    dispatch(loadQueue({ view: nextView, search, page: 1 }));
  };

  const page = useSelector(selectQueuePage);
  const pageSize = useSelector(selectQueuePageSize);
  // const total = useSelector(selectQueueTotal);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const goToPage = (nextPage) => {
    dispatch(loadQueue({ view, search, page: nextPage }));
  };

  return (
    <div className="queue-page">
      <div className="queue-toolbar">
        <div className="queue-tabs" role="tablist">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              role="tab"
              aria-selected={view === v.key}
              className={`queue-tab ${view === v.key ? 'queue-tab--active' : ''}`}
              onClick={() => changeView(v.key)}>
              {v.label}
            </button>
          ))}
        </div>

        <div className="queue-search">
          <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
            <path
              d="M10.5 10.5L14 14"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="search"
            className="queue-search__input"
            placeholder="Reference, name or PAN"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search applications"
          />
        </div>
      </div>

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}

      <div className="queue-table">
        <div className="queue-head">
          <span>Reference</span>
          <span>Applicant</span>
          <span>Amount</span>
          <span>EMI</span>
          <span>FOIR</span>
          <span>Status</span>
          <span>Reviewer</span>
        </div>

        {loading && queue.length === 0 && (
          <div className="queue-empty">
            <span className="queue-empty__spinner" aria-hidden="true" />
            <span>Loading applications…</span>
          </div>
        )}

        {!loading && queue.length === 0 && (
          <div className="queue-empty">
            <span className="queue-empty__title">Nothing here</span>
            <span className="queue-empty__text">
              {search
                ? 'No applications match that search.'
                : view === 'unclaimed'
                  ? 'Every submitted application has been picked up.'
                  : view === 'mine'
                    ? 'You have no applications assigned. Pick one up from Unclaimed.'
                    : 'No applications in this view yet.'}
            </span>
          </div>
        )}

        {queue.map((item) => (
          <QueueRow
            key={item.id}
            item={item}
            currentUserId={user?.id}
            onOpen={(id) => navigate(`/review/${id}`)}
          />
        ))}
      </div>
      {totalPages > 1 && (
        <div className="q-pagination">
          <button
            type="button"
            className="q-pagination__btn"
            disabled={page <= 1}
            onClick={() => goToPage(page - 1)}>
            ← Previous
          </button>

          <span className="q-pagination__info numeric">
            Page {page} of {totalPages} · {total} total
          </span>

          <button
            type="button"
            className="q-pagination__btn"
            disabled={page >= totalPages}
            onClick={() => goToPage(page + 1)}>
            Next →
          </button>
        </div>
      )}

      {/* {total > 0 && (
        <p className="queue-count numeric">
          {queue.length} of {total}
        </p>
      )} */}
    </div>
  );
};

export default QueuePage;
